import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import { useState, useEffect, useContext } from 'react';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import EmployeeService from '../../../../services/Employee';
import isAdmin from '../../../../helpers/isAdmin';
import isDirector from '../../../../helpers/isDirector';
import isEducationDirectorAdminAssistant from '../../../../helpers/isEducationDirectorAdminAssistant';
import UserInterface from '../../../../interfaces/UserInterface';
import { createOptionsPage } from '../../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../../entities/PagedMeta';

interface UserSelectionModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: (selectedUsers: UserWithEmployee[]) => void;
}

export interface UserWithEmployee extends UserInterface {
  employee?: {
    id: string;
    title?: { name: string };
    department?: { name: string };
    supervisor?: { name: string };
    startDate?: string;
  };
}

const UserSelectionModal = ({ isOpen, toggle, onConfirm }: UserSelectionModalProps) => {
  const { token, currentSchool, user: loggedUser }: any = useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<UserWithEmployee[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedUsersMap, setSelectedUsersMap] = useState<Map<string, UserWithEmployee>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const loadUsers = async (search: string) => {
    if (!currentSchool?.value || !token) {
      return;
    }

    try {
      setIsLoading(true);
      const employeeService = EmployeeService(token);
      const filteredOptions = {
        filters: [
          {
            field: 'status',
            operation: 'in',
            value: ['ACTIVE', 'LOA', 'HIRED', 'PENDING'],
          },
        ],
        options: createOptionsPage(createPagedMeta(), {
          limit: search.trim() ? 1000 : 100,
          sortDesc: 'asc',
        }),
      };
      const response = await employeeService.listEmployeesBySchool(currentSchool.value, search, filteredOptions);

      const ACTIVE_STATUSES = ['ACTIVE', 'LOA', 'HIRED', 'PENDING'];
      const employeesList = response.data || [];

      let visibleEmployees = employeesList.filter((emp: any) => emp.user && ACTIVE_STATUSES.includes(emp.status));

      if (!isAdmin(loggedUser)) {
        if (isDirector(loggedUser)) {
          visibleEmployees = visibleEmployees.filter((emp: any) => emp.supervisorId === loggedUser.id);
        }
        visibleEmployees = visibleEmployees.filter((emp: any) => {
          if (emp.user?.roles?.includes('admin')) return false;
          if (emp.userId === loggedUser?.id) return false;
          if (
            isEducationDirectorAdminAssistant(loggedUser) &&
            (isEducationDirectorAdminAssistant(emp.user) || isDirector(emp.user))
          )
            return false;
          return true;
        });
      }

      const usersWithEmployee: UserWithEmployee[] = visibleEmployees
        .map((emp: any) => ({
          ...emp.user,
          employee: {
            id: emp.id,
            title: emp.title,
            department: emp.department,
            supervisor: emp.supervisor,
            startDate: emp.startDate,
          },
        }))
        .sort((a: UserWithEmployee, b: UserWithEmployee) => a.firstName.localeCompare(b.firstName));

      setUsers(usersWithEmployee);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentSchool?.value) {
      setSearchTerm('');
      setSelectedUsers(new Set());
      setSelectedUsersMap(new Map());
      loadUsers('');
    }
  }, [isOpen, currentSchool?.value]);

  useEffect(() => {
    if (!isOpen || !currentSchool?.value) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      loadUsers(searchTerm);
    }, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  const toggleUser = (userId: string, userObj: UserWithEmployee) => {
    const newSelected = new Set(selectedUsers);
    const newMap = new Map(selectedUsersMap);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
      newMap.delete(userId);
    } else {
      newSelected.add(userId);
      newMap.set(userId, userObj);
    }
    setSelectedUsers(newSelected);
    setSelectedUsersMap(newMap);
  };

  const handleConfirm = () => {
    const selected = Array.from(selectedUsersMap.values());
    if (selected.length > 0) {
      onConfirm(selected);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSelectedUsers(new Set());
    setSelectedUsersMap(new Map());
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Select Users</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="userSearch">Search User</Label>
          <Input
            type="text"
            id="userSearch"
            placeholder="Type user name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
          />
        </FormGroup>

        <div className="mt-3">
          <Label>Select Users ({selectedUsers.size} selected)</Label>
          <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
            {isLoading ? (
              <div className="text-center py-4">
                <i className="fas fa-spinner fa-spin mr-2" />
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div className="text-center text-muted py-4">
                {searchTerm ? 'No users found matching your search' : 'No users available'}
              </div>
            ) : (
              <ListGroup flush>
                {users.map((user) => (
                  <ListGroupItem
                    key={user.id}
                    tag="button"
                    action
                    onClick={() => toggleUser(user.id, user)}
                    className="d-flex align-items-center"
                    style={{ cursor: 'pointer' }}
                  >
                    <Input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => {}}
                      className="mr-3 ml-1"
                      style={{ position: 'relative', marginTop: 0 }}
                    />
                    <div className="flex-grow-1 d-flex align-items-center">
                      <div className="w-100 d-flex justify-content-between align-items-center">
                        <div className="font-weight-bold">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-muted small d-flex align-items-center">
                          {user.email && <span>{user.email}</span>}
                          {user.email && user.employee?.title?.name && <span className="mx-3">•</span>}
                          {user.employee?.title?.name && <span>{user.employee.title.name}</span>}
                          {user.email && user.employee?.department?.name && <span className="mx-3">•</span>}
                          {user.employee?.department?.name && <span>{user.employee.department.name}</span>}
                        </div>
                      </div>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            )}
          </div>
        </div>

        {selectedUsers.size > 0 && (
          <div className="mt-3 alert alert-info">
            <i className="fas fa-info-circle mr-2" />
            {selectedUsers.size === 1
              ? '1 user selected'
              : `${selectedUsers.size} users selected. You will fill the form for each one.`}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={selectedUsers.size === 0}>
          <i className="fas fa-plus mr-2" />
          Create New
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserSelectionModal;
